import React, { useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Plus, Pencil, Trash2, ArrowUp, ArrowDown, X, FileText, HeartPulse, ShieldCheck,
  FolderKanban, ClipboardList, Flame, Siren, Wrench, Heading2, AlignLeft,
  CheckSquare, MessageSquarePlus, TextCursorInput, CalendarDays, PenTool,
  ToggleLeft, Hash, List, Camera, Image as ImageIcon, Table2,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Icônes utilisables sur la carte d'une fiche - "icon" stocké côté serveur
// est juste une chaîne, ce registre associe chaque nom à un composant.
export const TEMPLATE_ICONS = { FileText, HeartPulse, ShieldCheck, FolderKanban, ClipboardList, Flame, Siren, Wrench };
export const iconFor = (name) => TEMPLATE_ICONS[name] || FileText;

const PDF_THEMES = [
  { key: 'blue', label: 'Bleu', swatch: '#312783' },
  { key: 'green', label: 'Vert', swatch: '#27832B' },
  { key: 'red', label: 'Rouge', swatch: '#B91C1C' },
  { key: 'orange', label: 'Orange', swatch: '#EA580C' },
  { key: 'purple', label: 'Violet', swatch: '#7C3AED' },
  { key: 'teal', label: 'Turquoise', swatch: '#0D9488' },
  { key: 'pink', label: 'Rose', swatch: '#DB2777' },
];

const COLOR_OPTIONS = [
  'bg-blue-700', 'bg-green-700', 'bg-red-700', 'bg-amber-600',
  'bg-purple-700', 'bg-teal-700', 'bg-pink-700', 'bg-slate-700',
];

// Palette volontairement réduite à 5 éléments simples pour des utilisateurs
// sans bagage informatique (la signature est ajoutée automatiquement en fin
// de fiche, elle ne fait plus partie des choix). Chaque élément correspond
// à un ou plusieurs types de blocs côté serveur (voir SIMPLE_KIND_TO_BACKEND
// et BACKEND_TYPE_TO_KIND) : la fusion se fait uniquement dans cet éditeur,
// le reste de l'application (rendu de la fiche, génération du PDF) continue
// de fonctionner avec les types de blocs existants, donc les anciennes fiches
// déjà créées ne sont pas affectées tant qu'elles ne sont pas réenregistrées.
const BLOCK_TYPES = [
  { kind: 'section', label: 'Titre de section', hint: 'Regroupe les éléments suivants sous un intitulé', icon: Heading2, hasKey: false },
  { kind: 'control', label: 'Point de contrôle', hint: 'Conforme / Non conforme, avec une remarque possible', icon: CheckSquare, hasKey: true },
  { kind: 'yesno', label: 'Oui / Non', hint: 'Réponse Oui ou Non, sans notion de conformité', icon: ToggleLeft, hasKey: true },
  { kind: 'question', label: 'Question', hint: "Réponse libre de l'agent (courte ou longue)", icon: TextCursorInput, hasKey: true },
  { kind: 'number', label: 'Nombre', hint: 'Réponse chiffrée (quantité, température...)', icon: Hash, hasKey: true },
  { kind: 'choice', label: 'Liste de choix', hint: "L'agent choisit une option dans une liste que vous définissez", icon: List, hasKey: true },
  { kind: 'date', label: 'Date', hint: '', icon: CalendarDays, hasKey: true },
  { kind: 'table', label: 'Tableau', hint: "Tableau à colonnes dont vous choisissez la taille, que l'agent remplit ligne par ligne", icon: Table2, hasKey: true },
  { kind: 'photo', label: 'Photo', hint: "L'agent ajoute une photo à cet endroit de la fiche", icon: Camera, hasKey: true },
  { kind: 'info', label: 'Instruction', hint: 'Texte informatif, non modifiable par l\'agent', icon: AlignLeft, hasKey: false },
];
const blockTypeInfo = (kind) => BLOCK_TYPES.find((t) => t.kind === kind) || BLOCK_TYPES[0];

// Les blocs qui portent une réponse de l'agent peuvent être marqués
// obligatoires (voir missing_required_doc_terrain_fields côté serveur) - un
// titre de section ou une instruction n'ont pas de réponse, donc pas de case
// "Obligatoire".
const canBeRequired = (kind) => !['section', 'info'].includes(kind);

// Chaque type de bloc côté serveur est rattaché à un seul élément simple ici.
// title/subtitle, checkbox/checkbox_obs/observation et text_field/textarea_field
// partagent respectivement le même mode de stockage des réponses (ou aucune
// réponse), donc regrouper ces types ne touche à aucune donnée déjà saisie.
const BACKEND_TYPE_TO_KIND = {
  title: 'section', subtitle: 'section',
  checkbox: 'control', checkbox_obs: 'control', observation: 'control',
  yesno: 'yesno',
  text_field: 'question', textarea_field: 'question',
  number_field: 'number',
  select: 'choice',
  date_field: 'date',
  photo: 'photo',
  table: 'table',
  text: 'info',
};

// Clé technique purement interne (jamais affichée) : générée une seule fois,
// à la création du bloc, et plus jamais modifiée ensuite - y compris si le
// libellé change - pour ne jamais faire disparaître une réponse déjà saisie.
const makeKey = () => `k${Date.now()}${Math.floor(Math.random() * 10000)}`;

// Convertit les blocs du modèle (types serveur) vers la représentation
// simplifiée de cet éditeur - la signature est retirée ici, elle est
// réinjectée automatiquement par toBackendBlocks() à l'enregistrement.
// "withConforme" est conservé mais jamais montré : un ancien bloc
// "Observation seule" (sans Conforme/Non conforme) ne doit jamais se
// retrouver réétiqueté "NON CONFORME" dans un PDF déjà émis simplement
// parce qu'il a été ouvert dans ce constructeur - seuls les nouveaux
// "Point de contrôle" ont Conforme/Non conforme par défaut.
const toSimpleBlocks = (blocks) => (blocks || [])
  .filter((b) => b.type !== 'signature')
  .map((b) => ({
    id: b.id,
    kind: BACKEND_TYPE_TO_KIND[b.type] || 'info',
    label: b.label || '',
    key: b.key,
    long: b.type === 'textarea_field',
    withConforme: b.type !== 'observation',
    options: b.options || undefined,
    required: !!b.required,
    columns: b.columns || undefined,
  }));

// Convertit la représentation simplifiée vers les types de blocs serveur,
// et ajoute toujours une signature unique en toute fin de fiche.
const toBackendBlocks = (simpleBlocks) => {
  const converted = simpleBlocks.map((b) => {
    const required = canBeRequired(b.kind) ? !!b.required : undefined;
    switch (b.kind) {
      case 'section': return { id: b.id, type: 'subtitle', label: b.label };
      case 'control': return { id: b.id, type: b.withConforme === false ? 'observation' : 'checkbox_obs', label: b.label, key: b.key, required };
      case 'yesno': return { id: b.id, type: 'yesno', label: b.label, key: b.key, required };
      case 'question': return { id: b.id, type: b.long ? 'textarea_field' : 'text_field', label: b.label, key: b.key, required };
      case 'number': return { id: b.id, type: 'number_field', label: b.label, key: b.key, required };
      case 'choice': return { id: b.id, type: 'select', label: b.label, key: b.key, options: (b.options || []).map((o) => o.trim()).filter(Boolean), required };
      case 'date': return { id: b.id, type: 'date_field', label: b.label, key: b.key, required };
      case 'photo': return { id: b.id, type: 'photo', label: b.label, key: b.key, required };
      case 'table': return { id: b.id, type: 'table', label: b.label, key: b.key, required, columns: (b.columns || []).map((c) => ({ name: c.name.trim(), width: c.width })) };
      default: return { id: b.id, type: 'text', label: b.label };
    }
  });
  converted.push({ id: `sig-${Date.now()}`, type: 'signature', label: '' });
  return converted;
};

const emptyDraft = () => ({
  label: '', description: '', icon: 'FileText', color: 'bg-blue-700', pdf_theme: 'blue', blocks: [],
});

// Menu de gestion des modèles de fiche Doc Terrain (admin/chef de poste) :
// liste des modèles existants (y compris ceux fournis avec l'application,
// modifiables comme les autres), création/édition via un constructeur de
// blocs (titre, sous-titre, texte, case à cocher, champ texte...) avec un
// onglet "Aperçu" pour voir la fiche telle qu'un agent la remplira. Modifier
// un modèle ne touche jamais aux documents déjà rédigés par les agents.
const TemplateManager = ({ open, onOpenChange, templates, onReload }) => {
  const [editingTemplate, setEditingTemplate] = useState(null); // null = liste, {} = nouveau, objet = édition
  const [draft, setDraft] = useState(emptyDraft());
  const [saving, setSaving] = useState(false);

  const startNew = () => { setDraft(emptyDraft()); setEditingTemplate({}); };
  const startEdit = (tpl) => {
    setDraft({ label: tpl.label, description: tpl.description || '', icon: tpl.icon || 'FileText', color: tpl.color || 'bg-blue-700', pdf_theme: tpl.pdf_theme || 'blue', blocks: toSimpleBlocks(tpl.blocks) });
    setEditingTemplate(tpl);
  };
  const backToList = () => { setEditingTemplate(null); setDraft(emptyDraft()); };

  const addBlock = (kind) => {
    const info = blockTypeInfo(kind);
    setDraft((prev) => ({
      ...prev,
      blocks: [...prev.blocks, {
        id: `new-${Date.now()}`, kind, label: '',
        key: info.hasKey ? makeKey() : undefined,
        long: false, required: false,
        options: kind === 'choice' ? ['Option 1', 'Option 2'] : undefined,
        columns: kind === 'table' ? [{ name: 'Colonne 1', width: 2 }, { name: 'Colonne 2', width: 2 }] : undefined,
      }]
    }));
  };
  const updateBlock = (idx, patch) => setDraft((prev) => ({
    ...prev,
    blocks: prev.blocks.map((b, i) => (i === idx ? { ...b, ...patch } : b))
  }));
  const removeBlock = (idx) => setDraft((prev) => ({ ...prev, blocks: prev.blocks.filter((_, i) => i !== idx) }));
  const moveBlock = (idx, dir) => {
    const newIdx = idx + dir;
    if (newIdx < 0 || newIdx >= draft.blocks.length) return;
    setDraft((prev) => {
      const blocks = [...prev.blocks];
      [blocks[idx], blocks[newIdx]] = [blocks[newIdx], blocks[idx]];
      return { ...prev, blocks };
    });
  };

  const handleSave = async () => {
    if (!draft.label.trim()) { toast.error('Donnez un nom au modèle'); return; }
    const missingLabel = draft.blocks.find((b) => !b.label?.trim());
    if (missingLabel) { toast.error(`Complétez le texte du bloc "${blockTypeInfo(missingLabel.kind).label}"`); return; }
    const badChoice = draft.blocks.find((b) => b.kind === 'choice' && (b.options || []).map((o) => o.trim()).filter(Boolean).length < 2);
    if (badChoice) { toast.error(`La liste de choix "${badChoice.label}" doit avoir au moins deux options`); return; }

    const badTable = draft.blocks.find((b) => b.kind === 'table' && (b.columns || []).some((c) => !c.name.trim()));
    if (badTable) { toast.error(`Donnez un titre à chaque colonne du tableau "${badTable.label}"`); return; }

    setSaving(true);
    const token = localStorage.getItem('token');
    const payload = {
      label: draft.label.trim(),
      description: draft.description.trim(),
      icon: draft.icon,
      color: draft.color,
      pdf_theme: draft.pdf_theme,
      blocks: toBackendBlocks(draft.blocks),
    };
    try {
      const isEditing = editingTemplate && editingTemplate.id;
      const res = await fetch(`${BACKEND_URL}/api/doc-terrain-templates${isEditing ? `/${editingTemplate.id}` : ''}`, {
        method: isEditing ? 'PUT' : 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      if (res.ok) {
        toast.success(isEditing ? 'Modèle modifié' : 'Modèle créé');
        backToList();
        onReload();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Erreur lors de l\'enregistrement');
      }
    } catch {
      toast.error('Erreur de connexion');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (tpl) => {
    if (!window.confirm(`Supprimer définitivement le modèle "${tpl.label}" ? Les documents déjà créés avec ce modèle resteront consultables.`)) return;
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${BACKEND_URL}/api/doc-terrain-templates/${tpl.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) { toast.success('Modèle supprimé'); onReload(); }
      else { const err = await res.json(); toast.error(err.detail || 'Erreur lors de la suppression'); }
    } catch {
      toast.error('Erreur de connexion');
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) backToList(); }}>
      <DialogContent className="max-w-4xl max-h-[88vh] overflow-y-auto">
        {!editingTemplate ? (
          <>
            <DialogHeader>
              <DialogTitle>Modèles de fiche Doc Terrain</DialogTitle>
            </DialogHeader>
            <p className="text-sm text-slate-500 dark:text-slate-400">
              Créez ou modifiez librement les modèles de fiche, y compris ceux fournis avec l'application.
              Modifier un modèle ne change jamais les documents déjà rédigés par les agents.
            </p>
            <Button onClick={startNew} className="btn-primary w-full" data-testid="new-template-btn">
              <Plus className="h-4 w-4 mr-2" /> Nouveau modèle
            </Button>
            <div className="space-y-2">
              {templates.map((tpl) => (
                <Card key={tpl.id} className="glass-effect" data-testid={`template-row-${tpl.id}`}>
                  <CardContent className="p-3 flex items-center justify-between gap-2">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-medium text-sm text-slate-800 dark:text-slate-100">{tpl.label}</p>
                        {tpl.is_base && <Badge variant="outline" className="text-[10px]">Fournie avec l'application</Badge>}
                      </div>
                      {tpl.description && <p className="text-xs text-slate-500 dark:text-slate-400 truncate">{tpl.description}</p>}
                    </div>
                    <div className="flex gap-1.5 shrink-0">
                      <Button variant="outline" size="sm" onClick={() => startEdit(tpl)} data-testid={`edit-template-${tpl.id}`}>
                        <Pencil className="h-3.5 w-3.5 mr-1" /> Modifier
                      </Button>
                      <Button variant="outline" size="sm" className="text-red-600 hover:text-red-700" onClick={() => handleDelete(tpl)} data-testid={`delete-template-${tpl.id}`}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>{editingTemplate.id ? `Modifier — ${editingTemplate.label}` : 'Nouveau modèle'}</DialogTitle>
            </DialogHeader>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs">Nom de la fiche *</Label>
                <Input value={draft.label} onChange={(e) => setDraft({ ...draft, label: e.target.value })} placeholder="Ex : Fiche ronde de nuit" data-testid="template-label" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Description</Label>
                <Input value={draft.description} onChange={(e) => setDraft({ ...draft, description: e.target.value })} placeholder="Une phrase pour expliquer à quoi elle sert" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Icône</Label>
                <div className="flex items-center gap-1.5 flex-wrap">
                  {Object.entries(TEMPLATE_ICONS).map(([name, Icon]) => (
                    <button
                      key={name}
                      type="button"
                      onClick={() => setDraft({ ...draft, icon: name })}
                      title={name}
                      className={`h-8 w-8 rounded-lg flex items-center justify-center border transition-colors ${
                        draft.icon === name
                          ? 'bg-indigo-600 border-indigo-600 text-white'
                          : 'border-slate-200 dark:border-slate-700 text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800'
                      }`}
                    >
                      <Icon className="h-4 w-4" />
                    </button>
                  ))}
                </div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Couleur</Label>
                <div className="flex items-center gap-1.5 flex-wrap pt-1">
                  {COLOR_OPTIONS.map((c) => (
                    <button
                      key={c}
                      type="button"
                      onClick={() => setDraft({ ...draft, color: c })}
                      className={`h-7 w-7 rounded-full ${c} border-2 ${draft.color === c ? 'border-slate-800 dark:border-white' : 'border-transparent'}`}
                      title={c}
                    />
                  ))}
                </div>
              </div>
              <div className="space-y-1.5 md:col-span-2">
                <Label className="text-xs">Couleur du PDF généré</Label>
                <div className="flex items-center gap-1.5 flex-wrap pt-1" data-testid="pdf-theme-picker">
                  {PDF_THEMES.map((t) => (
                    <button
                      key={t.key}
                      type="button"
                      onClick={() => setDraft({ ...draft, pdf_theme: t.key })}
                      style={{ backgroundColor: t.swatch }}
                      className={`h-7 w-7 rounded-full border-2 ${draft.pdf_theme === t.key ? 'border-slate-800 dark:border-white' : 'border-transparent'}`}
                      title={t.label}
                      data-testid={`pdf-theme-${t.key}`}
                    />
                  ))}
                  <span className="text-xs text-slate-500 dark:text-slate-400 ml-1">
                    {(PDF_THEMES.find(t => t.key === draft.pdf_theme) || PDF_THEMES[0]).label}
                  </span>
                </div>
              </div>
            </div>

            {/* Aperçu de la carte "Nouveau document" telle que les agents la verront */}
            <div className="flex items-center gap-2.5 p-2.5 rounded-lg border border-dashed border-slate-300 dark:border-slate-700">
              <div className={`w-8 h-8 ${draft.color} rounded-lg flex items-center justify-center shrink-0`}>
                {React.createElement(iconFor(draft.icon), { className: 'h-4 w-4 text-white' })}
              </div>
              <div className="min-w-0">
                <p className="text-sm font-medium text-slate-800 dark:text-slate-100 truncate">{draft.label || 'Nom de la fiche'}</p>
                <p className="text-xs text-slate-500 dark:text-slate-400 truncate">{draft.description || 'Description de la fiche'}</p>
              </div>
            </div>

            {/* Ajouter un élément et la fiche elle-même ne font qu'un : pas
                d'onglet "Aperçu" séparé - comme dans un traitement de texte,
                ce que l'on voit ici est déjà ce que l'agent remplira. */}
            <div className="space-y-1.5 pt-1">
              <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
                {BLOCK_TYPES.map((t) => (
                  <button
                    key={t.kind}
                    type="button"
                    onClick={() => addBlock(t.kind)}
                    title={t.hint}
                    className="flex flex-col items-center justify-center gap-1 p-2 rounded-lg border border-slate-200 dark:border-slate-700 hover:border-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-950/30 transition-colors text-center"
                    data-testid={`add-block-${t.kind}`}
                  >
                    <t.icon className="h-4 w-4 text-indigo-600" />
                    <span className="text-[11px] font-medium leading-tight text-slate-700 dark:text-slate-200">{t.label}</span>
                  </button>
                ))}
              </div>
            </div>

            <div className="rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-4">
              {draft.blocks.length === 0 ? (
                <p className="text-xs text-slate-400 text-center py-8">
                  Votre fiche est vide - cliquez sur un élément ci-dessus pour commencer.
                </p>
              ) : (
                <FicheCanvas blocks={draft.blocks} onUpdate={updateBlock} onRemove={removeBlock} onMove={moveBlock} />
              )}
              <SignaturePreview />
            </div>

            <div className="flex gap-3 pt-2">
              <Button variant="outline" className="flex-1" onClick={backToList}>Annuler</Button>
              <Button className="flex-1 btn-primary" onClick={handleSave} disabled={saving} data-testid="save-template-btn">
                {saving ? 'Enregistrement...' : 'Enregistrer le modèle'}
              </Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

// Illustration statique des deux boutons (Conforme/Non conforme ou Oui/Non)
// tels qu'ils apparaîtront réellement (voir ConformiteToggle dans DocTerrain.js).
const ConformiteTogglePreview = ({ labels = ['Conforme', 'Non conforme'] }) => (
  <div className="flex gap-1.5 shrink-0">
    <button type="button" disabled className="px-2.5 py-1 rounded-md text-xs font-semibold border border-slate-300 dark:border-slate-600 text-slate-500 dark:text-slate-400">
      {labels[0]}
    </button>
    <button type="button" disabled className="px-2.5 py-1 rounded-md text-xs font-semibold border border-slate-300 dark:border-slate-600 text-slate-500 dark:text-slate-400">
      {labels[1]}
    </button>
  </div>
);

// Case "Obligatoire" partagée par tous les éléments pouvant porter une
// réponse (voir canBeRequired) - affichée en petit sous le libellé, comme
// "Réponse longue" pour une question.
const RequiredToggle = ({ checked, onChange, testId }) => (
  <label className="flex items-center gap-1.5 text-[11px] text-slate-400 cursor-pointer w-fit">
    <Checkbox checked={!!checked} onCheckedChange={(c) => onChange(!!c)} className="h-3.5 w-3.5" data-testid={testId} />
    Obligatoire
  </label>
);

// Classes communes aux champs de libellé "invisibles" tant qu'on ne clique
// pas dessus - exactement l'effet recherché pour une édition "comme un
// traitement de texte" : on voit directement le rendu final de la fiche, le
// texte qu'on tape apparaît avec son habillage définitif (gras, italique...),
// et seul un léger soulignement au survol indique qu'un texte est modifiable.
const inlineFieldClasses = 'bg-transparent border-0 border-b border-dashed border-transparent rounded-none shadow-none px-0 hover:border-slate-300 dark:hover:border-slate-600 focus-visible:ring-0 focus-visible:border-indigo-400';

// Petites flèches (réordonner) et croix (supprimer) communes à chaque ligne -
// visibles en permanence mais discrètes, pas seulement au survol : au doigt
// sur tablette il n'y a pas de "survol", elles doivent rester cliquables.
const RowControls = ({ idx, total, onMove, onRemove, align = 'start' }) => (
  <div className={`flex items-center gap-1 shrink-0 ${align === 'start' ? 'order-first' : 'order-last'}`}>
    <button type="button" onClick={() => onMove(idx, -1)} disabled={idx === 0} className="disabled:opacity-20 text-slate-300 hover:text-slate-600 dark:hover:text-slate-300">
      <ArrowUp className="h-3.5 w-3.5" />
    </button>
    <button type="button" onClick={() => onMove(idx, 1)} disabled={idx === total - 1} className="disabled:opacity-20 text-slate-300 hover:text-slate-600 dark:hover:text-slate-300">
      <ArrowDown className="h-3.5 w-3.5" />
    </button>
    <button type="button" onClick={() => onRemove(idx)} className="text-slate-300 hover:text-red-600 ml-1">
      <X className="h-3.5 w-3.5" />
    </button>
  </div>
);

// La fiche en cours de construction, éditée directement dans son rendu final
// (comme un document) : pas de liste d'éléments séparée d'un aperçu, chaque
// ligne EST l'aperçu, avec son libellé modifiable en place. Regroupe les
// blocs "section" comme sections visuelles, exactement comme GenericFicheFields
// dans DocTerrain.js.
const FicheCanvas = ({ blocks, onUpdate, onRemove, onMove }) => {
  const groups = [];
  let current = { items: [] };
  groups.push(current);
  blocks.forEach((block, idx) => {
    if (block.kind === 'section') {
      current = { items: [], sectionIdx: idx, sectionBlock: block };
      groups.push(current);
    } else {
      current.items.push({ block, idx });
    }
  });

  const renderItem = ({ block, idx }) => {
    const total = blocks.length;
    const controls = <RowControls idx={idx} total={total} onMove={onMove} onRemove={onRemove} align="end" />;

    if (block.kind === 'info') {
      return (
        <div key={block.id} className="flex items-start gap-2 py-1">
          <Input
            value={block.label}
            onChange={(e) => onUpdate(idx, { label: e.target.value })}
            placeholder="Texte de l'instruction..."
            className={`flex-1 min-w-0 text-xs italic text-slate-500 dark:text-slate-400 ${inlineFieldClasses}`}
            data-testid={`block-label-${idx}`}
          />
          {controls}
        </div>
      );
    }
    const requiredRow = canBeRequired(block.kind) && (
      <RequiredToggle checked={block.required} onChange={(v) => onUpdate(idx, { required: v })} testId={`block-required-${idx}`} />
    );

    if (block.kind === 'control' || block.kind === 'yesno') {
      return (
        <div key={block.id} className="flex items-start gap-2 py-1.5 border-b border-slate-100 dark:border-slate-700 last:border-0">
          <div className="flex-1 min-w-0 space-y-1">
            <Input
              value={block.label}
              onChange={(e) => onUpdate(idx, { label: e.target.value })}
              placeholder={block.kind === 'yesno' ? 'Libellé de la question Oui/Non' : 'Libellé du point de contrôle'}
              className={`w-full text-sm ${inlineFieldClasses}`}
              data-testid={`block-label-${idx}`}
            />
            {requiredRow}
          </div>
          <div className="flex items-center gap-1.5 shrink-0 pt-1">
            {block.kind === 'yesno' ? (
              <ConformiteTogglePreview labels={['Oui', 'Non']} />
            ) : (
              <>
                <ConformiteTogglePreview />
                <Button type="button" variant="outline" size="sm" disabled className="h-7 text-xs gap-1.5">
                  <MessageSquarePlus className="h-3 w-3" /> Observation
                </Button>
              </>
            )}
          </div>
          {controls}
        </div>
      );
    }
    if (block.kind === 'date') {
      return (
        <div key={block.id} className="flex items-start gap-2 py-1.5">
          <div className="flex-1 min-w-0 space-y-1">
            <Input
              value={block.label}
              onChange={(e) => onUpdate(idx, { label: e.target.value })}
              placeholder="Libellé de la date"
              className={`w-full text-sm ${inlineFieldClasses}`}
              data-testid={`block-label-${idx}`}
            />
            {requiredRow}
          </div>
          <Input type="date" disabled className="w-36 h-8 shrink-0 opacity-70" />
          {controls}
        </div>
      );
    }
    if (block.kind === 'number') {
      return (
        <div key={block.id} className="flex items-start gap-2 py-1.5">
          <div className="flex-1 min-w-0 space-y-1">
            <Input
              value={block.label}
              onChange={(e) => onUpdate(idx, { label: e.target.value })}
              placeholder="Libellé du nombre"
              className={`w-full text-sm ${inlineFieldClasses}`}
              data-testid={`block-label-${idx}`}
            />
            {requiredRow}
          </div>
          <Input type="number" disabled placeholder="0" className="w-28 h-8 shrink-0 opacity-70" />
          {controls}
        </div>
      );
    }
    if (block.kind === 'choice') {
      const options = block.options || [];
      const updateOption = (optIdx, value) => onUpdate(idx, { options: options.map((o, i) => (i === optIdx ? value : o)) });
      const addOption = () => onUpdate(idx, { options: [...options, ''] });
      const removeOption = (optIdx) => onUpdate(idx, { options: options.filter((_, i) => i !== optIdx) });
      return (
        <div key={block.id} className="flex items-start gap-2 py-1.5">
          <div className="flex-1 min-w-0 space-y-1.5">
            <Input
              value={block.label}
              onChange={(e) => onUpdate(idx, { label: e.target.value })}
              placeholder="Libellé de la liste de choix"
              className={`w-full text-sm font-medium text-gray-600 dark:text-slate-300 ${inlineFieldClasses}`}
              data-testid={`block-label-${idx}`}
            />
            <div className="space-y-1 pl-1">
              {options.map((opt, optIdx) => (
                <div key={optIdx} className="flex items-center gap-1.5">
                  <Input
                    value={opt}
                    onChange={(e) => updateOption(optIdx, e.target.value)}
                    placeholder={`Option ${optIdx + 1}`}
                    className="h-7 text-xs"
                    data-testid={`block-option-${idx}-${optIdx}`}
                  />
                  <button type="button" onClick={() => removeOption(optIdx)} disabled={options.length <= 2} className="text-slate-300 hover:text-red-600 disabled:opacity-20 shrink-0">
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
              ))}
              <button type="button" onClick={addOption} className="text-[11px] text-indigo-600 hover:text-indigo-800 flex items-center gap-1">
                <Plus className="h-3 w-3" /> Ajouter une option
              </button>
            </div>
            {requiredRow}
          </div>
          {controls}
        </div>
      );
    }
    if (block.kind === 'table') {
      const columns = block.columns || [];
      const setColumns = (next) => onUpdate(idx, { columns: next });
      const updateColumn = (cIdx, patch) => setColumns(columns.map((c, i) => (i === cIdx ? { ...c, ...patch } : c)));
      const moveColumn = (cIdx, dir) => {
        const to = cIdx + dir;
        if (to < 0 || to >= columns.length) return;
        const next = [...columns];
        [next[cIdx], next[to]] = [next[to], next[cIdx]];
        setColumns(next);
      };
      return (
        <div key={block.id} className="flex items-start gap-2 py-1.5">
          <div className="flex-1 min-w-0 space-y-1.5">
            <Input
              value={block.label}
              onChange={(e) => onUpdate(idx, { label: e.target.value })}
              placeholder="Titre du tableau"
              className={`w-full text-sm font-medium text-gray-600 dark:text-slate-300 ${inlineFieldClasses}`}
              data-testid={`block-label-${idx}`}
            />
            <TableColumnsEditor
              columns={columns}
              onChangeName={(cIdx, name) => updateColumn(cIdx, { name })}
              onChangeWidth={(cIdx, width) => updateColumn(cIdx, { width })}
              onMove={moveColumn}
              onRemove={(cIdx) => setColumns(columns.filter((_, i) => i !== cIdx))}
              onAdd={() => setColumns([...columns, { name: `Colonne ${columns.length + 1}`, width: 2 }])}
              testIdPrefix={`block-col-${idx}`}
            />
            {requiredRow}
          </div>
          {controls}
        </div>
      );
    }
    if (block.kind === 'photo') {
      return (
        <div key={block.id} className="flex items-start gap-2 py-1.5">
          <div className="flex-1 min-w-0 space-y-1">
            <Input
              value={block.label}
              onChange={(e) => onUpdate(idx, { label: e.target.value })}
              placeholder="Libellé de la photo"
              className={`w-full text-sm font-medium text-gray-600 dark:text-slate-300 ${inlineFieldClasses}`}
              data-testid={`block-label-${idx}`}
            />
            <Button type="button" variant="outline" size="sm" disabled className="h-8 text-xs gap-1.5 opacity-70">
              <ImageIcon className="h-3.5 w-3.5" /> Ajouter une photo
            </Button>
            {requiredRow}
          </div>
          {controls}
        </div>
      );
    }
    // question
    return (
      <div key={block.id} className="flex items-start gap-2 py-1.5">
        <div className="flex-1 min-w-0 space-y-1">
          <Input
            value={block.label}
            onChange={(e) => onUpdate(idx, { label: e.target.value })}
            placeholder="Libellé de la question"
            className={`w-full text-sm font-medium text-gray-600 dark:text-slate-300 ${inlineFieldClasses}`}
            data-testid={`block-label-${idx}`}
          />
          {block.long ? (
            <Textarea disabled rows={2} placeholder="Réponse de l'agent..." className="opacity-70 text-sm" />
          ) : (
            <Input disabled placeholder="Réponse de l'agent..." className="opacity-70 h-8 text-sm" />
          )}
          <div className="flex items-center gap-3">
            <label className="flex items-center gap-1.5 text-[11px] text-slate-400 cursor-pointer w-fit">
              <Checkbox
                checked={!!block.long}
                onCheckedChange={(checked) => onUpdate(idx, { long: !!checked })}
                className="h-3.5 w-3.5"
                data-testid={`block-long-${idx}`}
              />
              Réponse longue
            </label>
            {requiredRow}
          </div>
        </div>
        {controls}
      </div>
    );
  };

  return (
    <div className="space-y-1">
      {groups.map((g, gIdx) => (
        <div key={gIdx} className={gIdx === 0 ? '' : 'mt-3 pt-3 border-t border-slate-100 dark:border-slate-800'}>
          {g.sectionBlock && (
            <div className="flex items-center gap-2 pb-1">
              <Input
                value={g.sectionBlock.label}
                onChange={(e) => onUpdate(g.sectionIdx, { label: e.target.value })}
                placeholder="Titre de section"
                className={`flex-1 min-w-0 text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400 ${inlineFieldClasses}`}
                data-testid={`block-label-${g.sectionIdx}`}
              />
              <RowControls idx={g.sectionIdx} total={blocks.length} onMove={onMove} onRemove={onRemove} align="end" />
            </div>
          )}
          {g.items.map(renderItem)}
        </div>
      ))}
    </div>
  );
};

export const COLUMN_WIDTHS = [
  { value: 1, label: 'Étroite' },
  { value: 2, label: 'Moyenne' },
  { value: 3, label: 'Large' },
  { value: 4, label: 'Très large' },
];

// Éditeur d'un tableau : l'en-tête est dessiné avec les largeurs réelles
// (chaque colonne occupe sa part du total, comme dans la fiche remplie et le
// PDF), le titre de chaque colonne se tape directement dessus, et sa taille se
// choisit juste dessous parmi quatre tailles nommées plutôt qu'en pourcentage.
const TableColumnsEditor = ({ columns, onChangeName, onChangeWidth, onMove, onRemove, onAdd, testIdPrefix }) => (
  <div className="space-y-1">
    <div className="flex border border-slate-300 dark:border-slate-600 rounded-md overflow-hidden">
      {columns.map((c, cIdx) => (
        <div key={cIdx} style={{ flex: `${c.width} 1 0%` }} className="min-w-0 border-r last:border-r-0 border-slate-300 dark:border-slate-600">
          <div className="bg-slate-100 dark:bg-slate-800 px-1">
            <Input
              value={c.name}
              onChange={(e) => onChangeName(cIdx, e.target.value)}
              placeholder="Titre"
              className={`h-8 text-xs font-semibold ${inlineFieldClasses}`}
              data-testid={`${testIdPrefix}-${cIdx}-name`}
            />
          </div>
          <div className="px-1 py-1.5 space-y-1">
            <select
              value={c.width}
              onChange={(e) => onChangeWidth(cIdx, Number(e.target.value))}
              className="w-full h-6 text-[11px] rounded border border-slate-200 dark:border-slate-700 bg-transparent"
              data-testid={`${testIdPrefix}-${cIdx}-width`}
            >
              {COLUMN_WIDTHS.map((w) => <option key={w.value} value={w.value}>{w.label}</option>)}
            </select>
            <div className="flex items-center justify-between text-slate-300">
              <button type="button" onClick={() => onMove(cIdx, -1)} disabled={cIdx === 0} className="disabled:opacity-20 hover:text-slate-600"><ArrowUp className="h-3 w-3 -rotate-90" /></button>
              <button type="button" onClick={() => onRemove(cIdx)} disabled={columns.length <= 1} className="disabled:opacity-20 hover:text-red-600" data-testid={`${testIdPrefix}-${cIdx}-remove`}><X className="h-3 w-3" /></button>
              <button type="button" onClick={() => onMove(cIdx, 1)} disabled={cIdx === columns.length - 1} className="disabled:opacity-20 hover:text-slate-600"><ArrowDown className="h-3 w-3 -rotate-90" /></button>
            </div>
          </div>
        </div>
      ))}
    </div>
    <div className="flex items-center gap-3">
      {columns.length < 8 && (
        <button type="button" onClick={onAdd} className="text-[11px] text-indigo-600 hover:text-indigo-800 flex items-center gap-1" data-testid={`${testIdPrefix}-add`}>
          <Plus className="h-3 w-3" /> Ajouter une colonne
        </button>
      )}
      <span className="text-[11px] text-slate-400">L'agent ajoutera les lignes en remplissant la fiche.</span>
    </div>
  </div>
);

// Aperçu statique de la signature, toujours affichée en bas de la fiche en
// cours de construction : elle n'est jamais un bloc à ajouter/retirer (voir
// toBackendBlocks), ce rappel visuel évite qu'on se demande où elle est passée.
const SignaturePreview = () => (
  <div className="grid grid-cols-1 md:grid-cols-3 gap-3 opacity-70 mt-3 pt-3 border-t border-slate-100 dark:border-slate-800">
    <div className="space-y-1.5">
      <Label className="text-xs font-medium text-gray-600 dark:text-slate-300">Nom de l'agent</Label>
      <Input disabled />
    </div>
    <div className="space-y-1.5">
      <Label className="text-xs font-medium text-gray-600 dark:text-slate-300">Date</Label>
      <Input type="date" disabled />
    </div>
    <div className="space-y-1.5">
      <Label className="text-xs font-medium text-gray-600 dark:text-slate-300">Signature</Label>
      <Button type="button" variant="outline" size="sm" disabled>
        <PenTool className="h-3.5 w-3.5 mr-1" /> Signer
      </Button>
    </div>
  </div>
);

export default TemplateManager;
